import type { UserRouteEntitlementsModel } from '.';
import {
    compareRows,
    findInheritedAccess,
    finishBulkWrite as finishBulkWriteAction,
    finishRouteWrite as finishRouteWriteAction,
    initialUserRouteEntitlementsState,
    isDirectlyEntitled,
    matchesSearch,
    setScope as setScopeAction,
    setSearch as setSearchAction,
    startBulkWrite as startBulkWriteAction,
    startRouteWrite as startRouteWriteAction,
    userRouteEntitlementsReducer,
} from './state';
import type {
    AccessEntity,
    RouteEntitlementRow,
    UserRouteEntitlementsAction,
    UserRouteEntitlementsState,
} from './state';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { Access, SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import { EmptyArray } from '@thinkalpha/common/util/empty.js';
import type { FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import type {
    DisambiguatedFirm,
    DisambiguatedGroup,
    DisambiguatedUser,
    Entity,
} from '@thinkalpha/ui-types/types/admin/index';
import type { Route } from '@thinkalpha/ui-types/types/admin/lab';
import { t } from 'i18next';
import { generateFirmNameToDisplay } from 'src/admin/firms/utils';
import { log } from 'src/admin/LAB/log';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getEntitiesByIdsQuery } from 'src/queries/entity';
import { getFirmsQuery } from 'src/queries/firms';
import { getAllRoutesQuery, invalidateRoutesQuery, setRoutePermission } from 'src/queries/labRoutes';
import { getUserAccessListQuery } from 'src/queries/user';
import type { ReactBindings } from 'src/types/bindings';

type Firms = FirmWithParentsAndChildren[];
type Subjects = Array<DisambiguatedFirm | DisambiguatedGroup | DisambiguatedUser>;

const entityName = (entity: DisambiguatedFirm | DisambiguatedGroup | DisambiguatedUser): string =>
    'name' in entity ? entity.name : (entity.username ?? `${entity.firstName} ${entity.lastName}`.trim());

@injectable()
export class UserRouteEntitlementsModelImpl implements UserRouteEntitlementsModel {
    readonly #disposableStack = new DisposableStack();
    readonly #state = signal<UserRouteEntitlementsState>(initialUserRouteEntitlementsState);
    /** Flips once `setScope` runs so no query fires before the user is known. */
    readonly #scopeSet = signal(false);
    /** Set on disposal so async write continuations bail out. */
    readonly #abort = new AbortController();
    readonly #queryClient: ReactBindings['QueryClient'];
    readonly #store: ReactBindings['Store'];

    readonly #routesQuery: QueryObserverModel<Route[], Error, Route[]>;
    readonly #firmsQuery: QueryObserverModel<Firms, Error, Firms>;
    readonly #accessListQuery: QueryObserverModel<SecurityPackage, DefaultError, Access[]>;
    readonly #subjectsQuery: QueryObserverModel<Entity[], Error, Subjects>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('Store') store: ReactBindings['Store'],
        @inject('CurrentUserModel') currentUser: ReactBindings['CurrentUserModel'],
    ) {
        this.#queryClient = queryClient;
        this.#store = store;
        // `GET /v1/routes` without a firm filter needs superuser mode, matching the previous panel.
        this.canManage = computed(() => currentUser.isSuperuser.value && currentUser.isSuperUserModeEnabled.value);
        const enabled = computed(() => this.#scopeSet.value && this.canManage.value);

        const routesOptions = computed<QueryObserverOptions<Route[], Error, Route[]>>(() => ({
            ...getAllRoutesQuery(),
            enabled: enabled.value,
        }));
        this.#routesQuery = queryObserverModelFactory<Route[], Error, Route[]>(routesOptions);
        this.#disposableStack.use(this.#routesQuery);

        const firmsOptions = computed<QueryObserverOptions<Firms, Error, Firms>>(() => ({
            ...getFirmsQuery(),
            enabled: enabled.value,
        }));
        this.#firmsQuery = queryObserverModelFactory<Firms, Error, Firms>(firmsOptions);
        this.#disposableStack.use(this.#firmsQuery);

        const accessListOptions = computed<QueryObserverOptions<SecurityPackage, DefaultError, Access[]>>(() => ({
            ...getUserAccessListQuery(this.#state.value.userId ?? undefined),
            enabled: enabled.value && this.#state.value.userId !== null,
        }));
        this.#accessListQuery = queryObserverModelFactory<SecurityPackage, DefaultError, Access[]>(accessListOptions);
        this.#disposableStack.use(this.#accessListQuery);

        // Groups and firms the user belongs to; their names label inherited rows.
        const subjectIds = computed<string[]>(() => {
            const accessList = this.#accessListQuery.signal.value.data;
            if (!accessList) return [];
            return accessList
                .filter((access) => access.type !== 'user' && access.type !== 'special')
                .map(({ id }) => id)
                .values()
                .unique()
                .toArray()
                .sort();
        });
        const subjectsOptions = computed<QueryObserverOptions<Entity[], Error, Subjects>>(() => ({
            ...getEntitiesByIdsQuery(subjectIds.value),
            enabled: enabled.value && subjectIds.value.length > 0,
        }));
        this.#subjectsQuery = queryObserverModelFactory<Entity[], Error, Subjects>(subjectsOptions);
        this.#disposableStack.use(this.#subjectsQuery);
    }

    #dispatch(action: UserRouteEntitlementsAction): void {
        this.#state.value = userRouteEntitlementsReducer(this.#state.value, action);
    }

    readonly canManage: ReadonlySignal<boolean>;
    readonly isLoading: ReadonlySignal<boolean> = computed(
        () =>
            this.#routesQuery.signal.value.isLoading ||
            this.#firmsQuery.signal.value.isLoading ||
            this.#accessListQuery.signal.value.isLoading ||
            this.#subjectsQuery.signal.value.isLoading,
    );
    // Without the access list or entity names an inherited route would look toggleable, so those
    // failures block the list too. A firms failure only degrades the firm label.
    readonly loadError: ReadonlySignal<boolean> = computed(
        () =>
            this.#routesQuery.signal.value.isError ||
            this.#accessListQuery.signal.value.isError ||
            this.#subjectsQuery.signal.value.isError,
    );
    readonly search: ReadonlySignal<string> = computed(() => this.#state.value.search);
    readonly bulkPending: ReadonlySignal<boolean> = computed(() => this.#state.value.bulkPending);

    readonly #allRows: ReadonlySignal<readonly RouteEntitlementRow[]> = computed(() => {
        const { userId, pendingRouteIds, bulkPending } = this.#state.value;
        const routes = this.#routesQuery.signal.value.data;
        if (!userId || !routes || this.loadError.value) return EmptyArray;
        const firms = this.#firmsQuery.signal.value.data ?? EmptyArray;
        const firmById = new Map(firms.map((firm) => [firm.id, firm] as const));
        const subjects = this.#subjectsQuery.signal.value.data ?? EmptyArray;
        const entities = new Map<string, AccessEntity>(
            subjects.map((subject) => [subject.id, { id: subject.id, name: entityName(subject), type: subject.type }]),
        );
        return routes
            .map((route): RouteEntitlementRow => {
                const inherited = findInheritedAccess(route, entities);
                const entitled = isDirectlyEntitled(route, userId);
                const firm = firmById.get(route.firmId);
                return {
                    id: route.id,
                    name: route.name,
                    firmName: firm ? generateFirmNameToDisplay(firm) : route.firmId,
                    entitled,
                    inherited,
                    pending: pendingRouteIds.includes(route.id) || (bulkPending && (inherited === null || entitled)),
                };
            })
            .sort(compareRows);
    });

    readonly rows: ReadonlySignal<readonly RouteEntitlementRow[]> = computed(() => {
        const search = this.#state.value.search.trim().toLowerCase();
        const rows = this.#allRows.value;
        return search ? rows.filter((row) => matchesSearch(row, search)) : rows;
    });
    readonly entitledCount: ReadonlySignal<number> = computed(
        () => this.#allRows.value.filter((row) => row.entitled || row.inherited !== null).length,
    );

    /** Rows that may receive a direct grant here: everything not granted through a group or firm. */
    readonly #grantableRows: ReadonlySignal<readonly RouteEntitlementRow[]> = computed(() =>
        this.#allRows.value.filter((row) => row.inherited === null),
    );
    readonly allEnabled: ReadonlySignal<boolean> = computed(() => {
        const rows = this.#grantableRows.value;
        return rows.length > 0 && rows.every((row) => row.entitled);
    });
    readonly noneEnabled: ReadonlySignal<boolean> = computed(() => !this.#allRows.value.some((row) => row.entitled));

    setScope(userId: string): void {
        if (this.#scopeSet.value && this.#state.value.userId !== userId) {
            throw new Error(
                'UserRouteEntitlementsModel scope is fixed after initialization; create a new instance for a different user',
            );
        }
        this.#dispatch(setScopeAction(userId));
        this.#scopeSet.value = true;
    }

    setSearch(value: string): void {
        this.#dispatch(setSearchAction(value));
    }

    async toggleRoute(routeId: string): Promise<void> {
        const row = this.#allRows.value.find(({ id }) => id === routeId);
        if (!row || row.pending || !this.canManage.value) return;
        // Inherited access is managed on the group or firm; only a redundant direct grant is removable.
        if (row.inherited !== null && !row.entitled) return;
        this.#dispatch(startRouteWriteAction(routeId));
        try {
            await this.#writeEntitlement(routeId, !row.entitled);
            this.#toast('success', t('admin::user::entitlements::routes::update-success', 'Route permission updated.'));
        } catch (error) {
            log.error(`Updating route entitlement ${routeId} (${row.name}) failed`, { error, routeId });
            this.#toast(
                'error',
                t(
                    'admin::user::entitlements::routes::update-error',
                    "There was an error updating the user's route permission.",
                ),
            );
        } finally {
            await this.#settleWrite(() => this.#dispatch(finishRouteWriteAction(routeId)));
        }
    }

    enableAll(): Promise<void> {
        return this.#writeAll(true);
    }

    clearAll(): Promise<void> {
        return this.#writeAll(false);
    }

    async #writeAll(allow: boolean): Promise<void> {
        if (this.#state.value.bulkPending || !this.canManage.value) return;
        const candidates = allow ? this.#grantableRows.value : this.#allRows.value;
        const targets = candidates.filter((row) => row.entitled !== allow && !row.pending);
        if (targets.length === 0) return;
        this.#dispatch(startBulkWriteAction());
        // Every write settles before the single cache refresh, so a late success is never shown stale.
        const results = await Promise.allSettled(targets.map((row) => this.#writeEntitlement(row.id, allow)));
        const failures = results.filter((result) => result.status === 'rejected');
        if (failures.length === 0) {
            this.#toast(
                'success',
                t('admin::user::entitlements::routes::update-all-success', 'Route permissions updated.'),
            );
        } else {
            log.error(`Updating ${failures.length} of ${targets.length} route entitlements failed`, {
                errors: failures.map(({ reason }) => reason),
                allow,
            });
            this.#toast(
                'error',
                t(
                    'admin::user::entitlements::routes::update-all-error',
                    "There was an error updating the user's route permissions.",
                ),
            );
        }
        await this.#settleWrite(() => this.#dispatch(finishBulkWriteAction()));
    }

    #writeEntitlement(routeId: string, allow: boolean): Promise<Route> {
        const userId = this.#state.value.userId;
        if (!userId) return Promise.reject(new Error('UserRouteEntitlementsModel has no user scope'));
        // `writer` is the permission the platform uses for "may use this route".
        return setRoutePermission({
            entityId: userId,
            entityType: 'user',
            accessType: 'writer',
            routeId,
            kind: allow ? 'allow' : 'deny',
        });
    }

    /** Refresh the shared routes cache, then clear the pending marker unless disposed mid-write. */
    async #settleWrite(finish: () => void): Promise<void> {
        await invalidateRoutesQuery(this.#queryClient);
        if (!this.#abort.signal.aborted) finish();
    }

    #toast(variant: 'success' | 'error', message: string): void {
        if (this.#abort.signal.aborted) return;
        this.#store.dispatch({ type: 'toast::enqueue', toast: { message, group: 'default', variant } });
    }

    [Symbol.dispose](): void {
        this.#abort.abort();
        this.#disposableStack.dispose();
    }
}
